import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@shared/components/ui/input';
import { PhoneInput } from '@shared/components/common/PhoneInput';
import { Private } from '@shared/components/common/Private';
import { useRestaurant } from '@core/tenant/TenantProvider';
import type { CompanionData } from '@features/booking/api/reservations';

const TREATMENT_OPTIONS = [
  { value: 'none', label: '' },
  { value: 'mr', label: 'Mr.' },
  { value: 'mrs', label: 'Mrs.' },
  { value: 'miss', label: 'Miss' },
  { value: 'ms', label: 'Ms.' },
  { value: 'dr', label: 'Dr.' },
] as const;

const RELATIONSHIP_OPTIONS = [
  { value: 'none', labelKey: 'GRAB_CONFIRMATION_SELECT', fallback: 'Select' },
  { value: 'spouse', labelKey: 'GRAB_CONFIRMATION_SPOUSE', fallback: 'Spouse' },
  { value: 'partner', labelKey: 'GRAB_CONFIRMATION_PARTNER', fallback: 'Partner' },
  { value: 'friend', labelKey: 'GRAB_CONFIRMATION_FRIEND', fallback: 'Friend' },
  { value: 'business', labelKey: 'GRAB_CONFIRMATION_BUSINESS', fallback: 'Business' },
  { value: 'son_or_daughter', labelKey: 'GRAB_CONFIRMATION_SON_OR_DAUGHTER', fallback: 'Son/Daughter' },
  { value: 'family_member', labelKey: 'GRAB_CONFIRMATION_FAMILY_MEMBER', fallback: 'Family member' },
  { value: 'other', labelKey: 'GRAB_CONFIRMATION_OTHER', fallback: 'Other' },
] as const;

interface CompanionInputsProps {
  maxCompanions: number;
  companions: CompanionData[];
  onChange: (companions: CompanionData[]) => void;
  onRemove?: (index: number, companion: CompanionData) => void;
}

/**
 * Companion input rows for party members.
 * Each companion has treatment, name, email, phone, notes,
 * and optionally "save as regular" with relationship.
 */
export function CompanionInputs({ maxCompanions, companions, onChange, onRemove }: CompanionInputsProps) {
  const { t } = useTranslation('booking');
  const restaurant = useRestaurant();

  const addCompanion = () => {
    if (companions.length < maxCompanions) {
      onChange([...companions, { name: '', treatment: 'none' }]);
    }
  };

  const removeCompanion = (index: number) => {
    const removed = companions[index];
    onChange(companions.filter((_, i) => i !== index));
    onRemove?.(index, removed);
  };

  const updateCompanion = (index: number, field: keyof CompanionData, value: string | boolean) => {
    const updated = companions.map((c, i) => {
      if (i === index) {
        const next = { ...c, [field]: value };
        // When unchecking isRegular, clear the relationship
        if (field === 'isRegular' && value === false) {
          next.relationship = undefined;
        }
        return next;
      }
      return c;
    });
    onChange(updated);
  };

  return (
    <div className="w-full space-y-3">
      <label className="text-sm font-medium text-foreground/70">
        {t('GRAB_CONFIRMATION_COMPANIONS')}
      </label>

      {companions.map((companion, idx) => (
        // Each companion's input card collects PII (name, email, phone,
        // notes). Wrap the card in <Private> so session-replay tools
        // never capture guest contact info.
        <Private key={idx} className="rounded-xl border border-border p-3 space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-xs text-foreground/50">
              {t('GRAB_CONFIRMATION_COMPANION')} {idx + 1}
            </p>
            <button
              type="button"
              onClick={() => removeCompanion(idx)}
              aria-label={t('A11Y_REMOVE_COMPANION', { number: idx + 1 })}
              className="text-foreground/40 hover:text-red-500 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>

          {/* Treatment + Name row */}
          <div className="flex gap-2">
            <select
              value={companion.treatment ?? 'none'}
              onChange={(e) => updateCompanion(idx, 'treatment', e.target.value)}
              aria-label={t('A11Y_SELECT_TREATMENT')}
              className="w-[100px] shrink-0 rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground focus:border-brand focus:outline-none"
            >
              {TREATMENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || t('GRAB_CONFIRMATION_TREATMENT')}
                </option>
              ))}
            </select>
            <Input
              type="text"
              placeholder={t('GRAB_CONFIRMATION_GUEST_NAME')}
              value={companion.name}
              onChange={(e) => updateCompanion(idx, 'name', e.target.value)}
              className="rounded-lg text-sm flex-1"
              required
              aria-required="true"
            />
          </div>

          {/* Email + Phone row */}
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="email"
              placeholder={t('GRAB_CONFIRMATION_GUEST_EMAIL')}
              value={companion.email ?? ''}
              onChange={(e) => updateCompanion(idx, 'email', e.target.value)}
              className="rounded-lg text-sm"
            />
            <PhoneInput
              value={companion.phone ?? ''}
              onChange={(val) => updateCompanion(idx, 'phone', val)}
              defaultCountry={restaurant.isoCountryCode}
              dialCode={restaurant.countryCode}
              className="rounded-lg border border-input px-3 py-2"
              inputClassName="text-sm"
            />
          </div>

          {/* Notes textarea */}
          <textarea
            placeholder={t('GRAB_CONFIRMATION_GUEST_NOTES_LABEL')}
            value={companion.notes ?? ''}
            onChange={(e) => updateCompanion(idx, 'notes', e.target.value)}
            className="w-full min-h-[60px] rounded-lg border border-border bg-background p-2 text-sm text-foreground placeholder:text-foreground/40 focus:border-brand focus:outline-none resize-none"
          />

          {/* Save as regular checkbox — wrapping <label> makes the whole row (input + gap + text) clickable. htmlFor is preserved for explicit a11y association. */}
          <label
            htmlFor={`companion-regular-${idx}`}
            className="flex items-center gap-2 cursor-pointer text-sm text-foreground/70"
          >
            <input
              type="checkbox"
              id={`companion-regular-${idx}`}
              checked={companion.isRegular ?? false}
              onChange={(e) => updateCompanion(idx, 'isRegular', e.target.checked)}
              className="rounded border-border"
            />
            <span>{t('GRAB_CONFIRMATION_CHECK_REGULAR_GUEST_LABEL')}</span>
          </label>

          {/* Relationship dropdown (shown when isRegular) */}
          {companion.isRegular && (
            <div className="space-y-1">
              <label className="text-xs text-foreground/50">
                {t('GRAB_CONFIRMATION_RELATIONSHIP')}
              </label>
              <select
                value={companion.relationship ?? 'none'}
                onChange={(e) => updateCompanion(idx, 'relationship', e.target.value)}
                aria-label={t('A11Y_SELECT_RELATIONSHIP')}
                className="w-full rounded-lg border border-border bg-background px-2 py-2 text-sm text-foreground focus:border-brand focus:outline-none"
              >
                {RELATIONSHIP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey, opt.fallback)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </Private>
      ))}

      {companions.length < maxCompanions && (
        <button
          type="button"
          onClick={addCompanion}
          className="flex items-center gap-1.5 text-sm text-brand hover:text-brand-dark transition-colors"
        >
          <Plus size={14} />
          {t('GRAB_CONFIRMATION_ADD_NEW_GUEST')}
        </button>
      )}
    </div>
  );
}
